import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'

type StatusBadgeColor =
  | 'gray'
  | 'green'
  | 'yellow'
  | 'red'
  | 'blue'
  | 'success'
  | 'neutral'
  | 'crimson'
type StatusBadgeSize = 'S' | 'L'

/** A status badge that displays some text, and has a color to indicate status */
export const StatusBadge: React.FC<{
  children: ReactNode
  color?: StatusBadgeColor
  badgeSize?: StatusBadgeSize
}> = ({ color = 'gray', badgeSize = 'S', children }) => (
  <StatusBadgeStyled data-testid='status-badge' data-size={badgeSize} data-color={color}>
    {children}
  </StatusBadgeStyled>
)

// ======
// Styles
// ======

const StatusBadgeStyled = styled.span({
  boxSizing: 'border-box',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: spacing.smx2,
  padding: spacing.smx2,

  fontFamily: font.family.primary,
  fontSize: font.size.sm.fontSize,
  lineHeight: font.size.sm.lineHeight,
  letterSpacing: font.size.sm.letterSpacing,
  fontWeight: font.weight.regular,

  '&[data-color="gray"]': {
    color: colors.neutral[900],
    backgroundColor: colors.neutral[100],
  },
  '&[data-color="green"]': {
    color: colors.success[600],
    backgroundColor: colors.success[50],
  },
  '&[data-color="yellow"]': {
    color: colors.warning[700],
    backgroundColor: colors.warning[50],
  },
  '&[data-color="red"]': {
    color: colors.error[600],
    backgroundColor: colors.error[50],
  },
  '&[data-color="crimson"]': {
    color: colors.white,
    backgroundColor: colors.crimson[500],
  },
  '&[data-color="blue"]': {
    color: colors.mariner[600],
    backgroundColor: colors.mariner[50],
  },
  '&[data-color="success"]': {
    color: colors.white,
    backgroundColor: colors.success[500],
  },
  '&[data-color="neutral"]': {
    color: colors.white,
    backgroundColor: colors.neutral[700],
  },

  '&[data-size="S"]': { borderRadius: radius.md, minHeight: '2rem' },
  '&[data-size="L"]': { borderRadius: radius.lg, minHeight: '3.2rem' },
})
